import { Box, Button, IconButton, Tooltip, Typography, useTheme } from '@mui/material'
import React, { useMemo, useRef, useState } from 'react'
import { observer } from 'mobx-react'

import learningSimba from '../../../../assets/simba-ai/studying.png'
import { useSimbaAIStore } from '../../../../store/simbaai.store'
import sadSimba from '../../../../assets/simba-ai/sad.png'
import { Icons } from '../../../../utils/utilities'
import { useSearchParams } from 'react-router-dom'
import AiResponseBox from '../../AiResponseBox'

const LearnModeHome = observer( ( { closeAiMentorDialog } ) => {


    const { layout, border, palette, shadows } = useTheme()

    const SimbaAIStore = useSimbaAIStore()

    const setSearchParams = useSearchParams().at( 1 )

    const setSearchParamsValues = ( values ) => {
        setSearchParams( prev => {
            const newParams = new URLSearchParams( prev )
            for ( const key in values ) {
                if ( !Object.hasOwn( values, key ) ) continue
                if ( values[key] )
                    newParams.set( key, values[key] )
                else
                    newParams.delete( key )
            }
            return newParams
        }, { replace: true } )
    }

    const showSubjectTopics = ( subject ) => {
        setSearchParamsValues( { selectedSubject: subject.subject_code } )
        SimbaAIStore.setView( "subject-learn-topics" )
    }

    const enterPracticeMode = () => {
        setSearchParamsValues( { simbaAIMode: 'practice' } )
        SimbaAIStore.setView( "home" )
    }

    const fetchAnswerFromAI = async ( topicDetails ) => {
        if ( SimbaAIStore.getAiResponsesQueue.request ) {
            SimbaAIStore.addResponse( { topic: SimbaAIStore.getAiResponsesQueue.request, response: SimbaAIStore.getAiResponsesQueue.response } )
            SimbaAIStore.setAiResponsesQueue( { request: "", response: "" } )
        }
        try {
            SimbaAIStore.setView( 'fetching-simba-ai-response', "left", false )
            SimbaAIStore.setSuggestedTopics( recentTopics, { category: "recentTopics", name: "Recent" } )
            if ( await SimbaAIStore.getAIResponse( topicDetails ) )
                SimbaAIStore.setView( 'simba-ai-response', "left", false )
            else
                SimbaAIStore.setView( 'simba-ai-response-error', "left", false )

        } catch ( err ) {
            console.log( err )
        }
    }

    const recentTopics = useMemo( () => {
        const recentPracticeSessions = SimbaAIStore.getPracticeSessions.slice( 0, 3 )
        const recentTopics = []
        for ( const ps of recentPracticeSessions ) {
            for ( const module in ps.modules_and_topics ) {
                if ( !Object.hasOwn( ps.modules_and_topics, module ) ) continue
                const topics = ps.modules_and_topics[module].topics
                recentTopics.push( ...topics.map( topic => ( { topic, module, subject_name: ps.subject.subject_name, subject_code: ps.subject.subject_code } ) ) )
            }
        }
        return recentTopics
    }, [SimbaAIStore.getPracticeSessions] )

    return (
        <Box className="slim-custom-scrollbar" flexGrow={1} overflow="auto" display="flex" flexDirection="column">
            {SimbaAIStore.getAiResponses.length > 0 && <Box padding={layout.pagePadding} paddingBottom="0" display="flex" flexDirection="column" gap={layout.cardsGap}>
                <Typography variant='subtitle2' component="p">Recently learned topics</Typography>
                {SimbaAIStore.getAiResponses.slice( -2 ).map( ( responseDetails, index ) => {
                    return ( <AiResponseBox key={index} responseDetails={responseDetails} /> )
                } )}
            </Box>}

            <Box flexGrow={1} display="flex" flexDirection="column" gap="10px" padding="20px">
                <Box width="100%" bgcolor={palette.contentBg} border={`4px solid ${palette.borderColor}`} borderRadius="20px" display="flex" flexDirection={{ xs: "column", sm: "row" }} gap={{ sm: "40px", xs: "10px" }} padding="20px 40px" alignItems="center"  >
                    <Box component="img" height={{ sm: "150px", xs: "100px" }} src={learningSimba} />
                    <Box display="flex" flexDirection="column" alignItems={{ xs: "center", sm: "flex-start" }}>
                        <Typography textAlign={{ xs: "center", sm: "left" }} variant='h5' fontSize={{ sm: "32px", xs: "24px" }} fontWeight="800" component="p" > Learn with Simba AI.</Typography>
                        <Typography textAlign={{ xs: "center", sm: "left" }} variant='subtitle2' component="p" display="block" maxWidth="450px" fontSize="12px">Select a subject and topic to receive AI-powered responses aligned with the subject’s syllabus.</Typography>
                        <Box marginTop="20px" flexWrap="wrap" flexGrow={{ xs: "1", sm: "initial" }} display="flex" gap={layout.cardsGap} alignItems="center" >
                            <Button onClick={enterPracticeMode} sx={{ borderBottom: `4px solid ${palette.primary.light + "aa"}`, borderRadius: "10px" }} startIcon={Icons.default.QuizIcon} variant='contained' disableElevation color='primary' >Enter practice mode</Button>
                        </Box>
                    </Box>
                </Box>

                {SimbaAIStore.getTopicsAvailableSubjects.length > 0 && <Box width="100%">
                    <Typography fontSize="14px" color='textSecondary' marginY="10px" fontWeight={600} component="p"   >
                        Select a subject to learn!
                    </Typography>
                    <Box display="grid" gridTemplateColumns={{ md: "repeat(3,1fr)", xs: "1fr", sm: "1fr 1fr" }} gap="10px" flexWrap="wrap" alignItems="center">
                        {SimbaAIStore.getTopicsAvailableSubjects.map( subject => (
                            <Box
                                onClick={() => showSubjectTopics( subject )}
                                component="button"
                                flexGrow={{ xs: 1, sm: "initial" }}
                                className="animation-wave"
                                overflow="hidden"
                                key={subject.subject_code}
                                display="flex"
                                gap="20px"
                                alignItems="center"
                                padding="10px 20px"
                                borderRadius="10px"
                                boxShadow={shadows[1]}
                                border={border[1]}
                                borderBottom={`4px solid ${palette.borderColor}`}
                                position="relative"
                                bgcolor={palette.contentBg}
                                sx={{
                                    cursor: "pointer",
                                    transition: "200ms",
                                    ":hover": {
                                        borderColor: palette.primary.light,
                                        transform: `skew(-5deg)`
                                    }
                                }}
                            >
                                <Box flexShrink={0} display="flex" fontSize="20px" justifyContent="center" width="40px" sx={{ aspectRatio: "1 / 1" }} borderRadius="50%" bgcolor={palette.primary.dark} color={palette.primary.contrastText} alignItems="center" height="100%">{Icons.default.Subjects}</Box>
                                <Box minWidth={0} display="flex" flexDirection="column" alignItems="flex-start">
                                    <Tooltip title={subject.subject_name}>
                                        <Typography textAlign="left" width="100%" fontSize={{ xs: "14px", sm: "18px" }} noWrap fontWeight={600}>{subject.subject_name}</Typography>
                                    </Tooltip>
                                    <Typography display="block" marginTop="-3px" fontSize="12px" component="em">{subject.subject_code}</Typography>
                                </Box>
                            </Box>
                        ) )}
                    </Box>
                </Box>}

                {SimbaAIStore.getPracticeSessions.length > 0 && <Box display="flex" flexDirection="column" width="100%" marginTop="10px">
                    <Typography fontSize="14px" color='textSecondary' marginY="10px" fontWeight={600} component="p"   >
                        Or select a recently added topics
                    </Typography>

                    <Box display="flex" gridTemplateColumns={{ md: "repeat(3,1fr)", xs: "1fr", sm: "1fr 1fr" }} flexWrap="wrap" width="100%" gap={layout.cardsGap}>
                        {recentTopics.map( ( topicDetails, index ) => {

                            return (
                                <Box
                                    component="button"
                                    onClick={() => fetchAnswerFromAI( topicDetails )}
                                    flexGrow={{ xs: 1, sm: "initial" }}
                                    className="animation-wave"
                                    overflow="hidden"
                                    key={topicDetails.topic + index}
                                    padding="10px 20px"
                                    borderRadius="10px"
                                    boxShadow={shadows[1]}
                                    border={border[1]}
                                    borderBottom={`4px solid ${palette.borderColor}`}
                                    position="relative"
                                    bgcolor={palette.contentBg}
                                    display="flex"
                                    flexDirection="column"
                                    alignItems="flex-start"
                                    sx={{
                                        cursor: "pointer",
                                        transition: "200ms",
                                        ":hover": {
                                            borderColor: palette.primary.light,
                                            transform: `skew(-5deg)`
                                        }
                                    }}
                                >
                                    <Typography fontWeight={600} title={topicDetails.topic} noWrap fontSize={{ sm: "16px", xs: "14px" }} color='textPrimary' >{topicDetails.topic}</Typography>
                                    <Typography component="em" display="flex" alignItems="center" gap="5px" title={topicDetails.subject_name} noWrap fontSize="12px" >{topicDetails.subject_name}</Typography>
                                </Box>
                            )
                        } )}
                    </Box>
                </Box>}

                {SimbaAIStore.getPracticeSessions.length === 0 && <Box position="relative" width="100%" border={`2px dashed ${palette.borderColor}`} borderRadius="20px" flexGrow={1} display="flex" flexDirection="column" alignItems="center" padding="20px" gap="10px" justifyContent="center" >
                    <Box position="absolute" height="60%" maxHeight="400px" bgcolor={palette.contentBg} borderRadius="50%" sx={{ aspectRatio: "1 / 1", zIndex: 1 }} />
                    <Box position="relative" sx={{ zIndex: 10 }} component="img" src={sadSimba} height="180px" alt="No practice sessions" />
                    <Typography position="relative" sx={{ zIndex: 10 }} textAlign="center" variant="h5" component="p" >No topics were added for your subjects!</Typography>
                </Box>}

            </Box>

            {/* {!loading && [''].includes( SimbaAIStore.getView.current ) && subjectSyllabus.details && subjectSyllabus.modules && <Box display="flex" border={border[1]} borderRadius={layout.borderRadius} margin="20px" flexDirection="column" gap="10px">
                <Box display="flex" gap="20px" justifyContent="space-between" bgcolor={palette.contentBg} padding="10px"  >
                    <Box>
                        <Typography variant="h6" fontSize="16px" component="h2">{subjectSyllabus.details.subject_name}[{subjectSyllabus.details.subject_name_short}]</Typography>
                        {subjectSyllabus.modules.length > 0 && <Typography variant="subtitle2" marginTop="-5px" fontSize="12px" color="GrayText"><em>Select a topic to start learn</em></Typography>}
                    </Box> 
                    <Button onClick={changeSubject} size='small' variant='contained' disableElevation color='secondary' startIcon={Icons.default.Sync} sx={{ textTransform: "capitalize", display: "flex" }} >Switch subject</Button>
                </Box>
                <Box padding="10px">
                    {subjectSyllabus.modules.length > 0 && <Box display="flex" flexDirection="column" gap="20px">
                        {subjectSyllabus.modules.map( ( module, i ) => (
                            <Box key={i} borderBottom={border[1]} paddingBottom="10px" >
                                <Typography color='primary' variant='subtitle2' fontSize="16" display="block" component="h3" gutterBottom>{module.name}</Typography>
                                <Box display="flex" flexWrap="wrap" alignItems="center" gap="10px"   >
                                    {module.chapters.map( chapter => chapter.topics.map( topic => ( { subject: subjectSyllabus.details, module, topic, chapterName: chapter.name } ) ) ).flat().map( ( topicDetails, i ) => (
                                        <Box onClick={() => fetchAnswerFromAI( topicDetails )} sx={{ cursor: "pointer", ":hover": { bgcolor: palette.rowHover } }} bgcolor={palette.contentBg} border={border[1]} key={i} padding="5px 10px" borderRadius="10px">
                                            <Typography variant="body2" fontSize="12px"><strong>{topicDetails.topic}</strong></Typography>
                                        </Box>
                                    ) )}
                                </Box>
                            </Box>
                        ) )}
                    </Box>}
                    {subjectSyllabus.modules.length === 0 && <Box border={`1px solid ${palette.warning.main}`} bgcolor={palette.warning.light + "22"} borderRadius={layout.borderRadius} padding="20px" display="flex" gap="20px" alignItems="center">
                        <Typography display="flex" gap="10px" alignItems="center" justifyContent="center" textAlign="center" flexGrow={1} variant="subtitle2" color="warning">
                            {Icons.default.Warning}
                            No syllabus data found for the selected subject
                        </Typography>
                    </Box>}
                </Box>
            </Box>} */}
        </Box>
    )
} )

export default LearnModeHome
